import React, { useEffect } from 'react';
import { Routes, Route, useParams, useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';

import TicketPickerPage from './views/ticket-picker/containers/TicketPickerPage';
import CartContainer from './views/cart/containers/CartContainer';
import ConfirmationPage from './views/confirmation/containers/ConfirmationPage';
import AuthContainer from "./views/auth/containers/AuthContainer";
import EmailContainer from "./views/auth/containers/EmailContainer";
import CouponContainer from "./views/coupon/containers/CouponContainer";
import SeriesDatePicker from "./views/series/components/SeriesDatePicker";
import CheckoutSteps from './views/checkout/containers/CheckoutSteps';
import KioskWaiverView from './views/questions/containers/KioskWaiverView';
import KioskDatePicker from './views/kiosk/containers/KioskDatePicker';
import KioskTimePicker from './views/kiosk/containers/KioskTimePicker';
import PickerWithDetailsPage from './views/picker-with-details/components/PickerWithDetails';
import DynamicListingPage from './views/dynamic-listing/components/DynamicListingPage';
import { TicketsReservedModal } from './views/picker-with-details/components/TicketsReserved';

import { fetchReservationIfNeeded, clearReservationData } from './actions/reservation';
import { setCheckoutOverlay, setConfirmation, goToListing } from './actions/ui';
import { getConfirmationData } from './actions/confirmation';
import { fetchEvent } from './actions/event';

import { getListingItem, setListingItem, removeListingItem } from '../listing/helpers/storage';
import { getReservation } from './selectors/reservation';
import { getMainEvent } from './selectors/event';
import { buildPathFromRouteName, withRouterCompat } from '@h/withRouterCompat';
import MemberLoginContainer from '@v/member/containers/MemberLoginContainer';
import ContinueShoppingModal from '@v/cart/components/ContinueShoppingModal';
import MoreTimeModal from '@v/checkout/components/MoreTimeModal';


let rehydratedRouterState = false;

function hasConfirmation(listing: string) {
  return JSON.parse(getListingItem(listing, "confirmation") || "false") === true;
}

function clearReservationWhenNavigatingFromConfirmation(listing: string) {
  const dispatch = useDispatch();
  const mainEvent = useSelector(getMainEvent);
  return () => {
    removeListingItem(listing, "confirmation");
    dispatch(clearReservationData({
      reset: true,
      clearChildEvent: true,
      fireCartChangeHooks: false,
    }));
    if (!mainEvent?.event?.series) {
      dispatch(fetchEvent());
    }
  };
}

function TicketPickerWrapper() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEmbed: boolean = useSelector((s: any) => s.ui.isEmbed);
  const currentRouteName: string = useSelector((s: any) => s.ui.currentRouteName);
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const isDynamicListing: boolean = useSelector((s: any) => s?.dynamicListing?.isDynamicListing);

  const token = getListingItem(listing, "token")

  const clearReservation = clearReservationWhenNavigatingFromConfirmation(listing);
  useEffect(() => {
    // go back to where router was at before the page was refreshed/reloaded
    if (isEmbed && !rehydratedRouterState) {
      rehydratedRouterState = true;
      if (currentRouteName !== 'ticketPicker') {
        const path = buildPathFromRouteName(currentRouteName, token ? { token } : {});
        navigate(path);
        return;
      }
    }

    if (hasConfirmation(listing)) {
      clearReservation();
      dispatch(setCheckoutOverlay(false));
    }
  }, []);

  return isDynamicListing ? <DynamicListingPage navigate={navigate} /> : <TicketPickerPage navigate={navigate} />;
}

function CheckoutWrapper() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const clearReservation = clearReservationWhenNavigatingFromConfirmation(listing);
  const reservation = useSelector(getReservation);

  useEffect(() => {
    window.scrollTo(0, 0);
    const getState = () => dispatch((_, getState) => getState());
    fetchReservationIfNeeded()(dispatch, getState)
        .catch(() => {
          return dispatch(goToListing());
        });
  }, []);

  useEffect(() => {
    if (reservation.reservation_id === undefined) {
      // reservation has not been fetched yet
      return;
    }
    if (hasConfirmation(listing)) {
      clearReservation();
      dispatch(goToListing());
      return;
    }
    if (reservationActive(reservation)) {
      dispatch(setCheckoutOverlay(true));
    } else {
      dispatch(goToListing());
    }
  }, [reservation]);

  return <CheckoutSteps navigate={navigate}/>;
}

export interface Reservation extends Record<string, any>{
  token: string;
}

const reservationActive = (reservation: Reservation) => (
  reservation.token !== ''
)

function CartWrapper() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const clearReservation = clearReservationWhenNavigatingFromConfirmation(listing);
  const reservation = useSelector(getReservation);

  useEffect(() => {
    const getState = () => dispatch((_, getState) => getState());
    fetchReservationIfNeeded()(dispatch, getState)
      .catch(() => {
        return dispatch(goToListing());
      });
  }, []);

  useEffect(() => {
    if (reservation.reservation_id === undefined) {
      // reservation has not been fetched yet
      return;
    }
    if (hasConfirmation(listing)) {
      clearReservation();
      dispatch(goToListing());
      return;
    }
    if (reservationActive(reservation)) {
      dispatch(setCheckoutOverlay(true));
    } else {
      dispatch(goToListing());
    }
  }, [reservation]);


  return <CartContainer navigate={navigate} />;
}

function ConfirmationWrapper() {
  const dispatch = useDispatch();
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const navigate = useNavigate();

  useEffect(() => {
    const getState = () => dispatch((_, getState) => getState());

    dispatch(setConfirmation(true));
    setListingItem(listing, "confirmation", JSON.stringify(true));
    getConfirmationData(getListingItem(listing, "token"), getListingItem(listing, "secret"))(dispatch, getState);

    fetchReservationIfNeeded(false)(dispatch, getState)
  }, []);

  return <ConfirmationPage navigate={navigate} />;
}

function PendingWrapper() {
  const dispatch = useDispatch();
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const reservation = useSelector(getReservation);
  const navigate = useNavigate()

  useEffect(() => {
    const getState = () => dispatch((_, getState) => getState());
    fetchReservationIfNeeded()(dispatch, getState)
      .catch(() => {
        return dispatch(goToListing());
      });
  }, []);

  useEffect(() => {
    setListingItem(listing, "confirmation", JSON.stringify(true));
    if (reservation.reservation_id === undefined) {
      // reservation has not been fetched yet
      return;
    }

    if (reservation?.token && !reservation?.isPendingPaymentCompletion && !reservation?.isCompleted) {
      removeListingItem(listing, "confirmation");
      navigate(`/checkout/${reservation.token}`);
    } else {
      dispatch(setConfirmation(true));
    }
    dispatch(setCheckoutOverlay(true));

  }, [reservation]);

  return <ConfirmationPage navigate={navigate} />;
}

function SeriesDatePickerWrapper() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const paymentMethod: number = useSelector((s: any) => s.payment.method);
  const isDynamicListing: boolean = useSelector((s: any) => s?.dynamicListing?.isDynamicListing);
  const DatePicker = paymentMethod === 13 ? KioskDatePicker : SeriesDatePicker;

  useEffect(() => {
    if (hasConfirmation(listing)) {
      removeListingItem(listing, "confirmation");
    }
    dispatch(setCheckoutOverlay(true));
  }, []);

  return isDynamicListing ? <DynamicListingPage navigate={navigate} /> : <DatePicker navigate={navigate} />;
}

function CouponWrapper() {
  const navigate = useNavigate();
  const listing: string = useSelector((s: any) => s.ui.listingSlug);
  const clearReservation = clearReservationWhenNavigatingFromConfirmation(listing);

  useEffect(() => {
    if (hasConfirmation(listing)) {
      clearReservation();
    }
  }, []);

  return <CouponContainer navigate={navigate} />;
}

function KioskWaiverWrapper() {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const listing: string = useSelector((s: any) => s.ui.listingSlug);
    const search = new URLSearchParams(useLocation().search);
    const question = findQuestionById(search.get("question"));

    useEffect(() => {
        if (hasConfirmation(listing)) {
            removeListingItem(listing, "confirmation");
            dispatch(setCheckoutOverlay(false));
        }
    }, []);

    return <KioskWaiverView question={question} navigate={navigate} />;
}

function findQuestionById(questionId) {
    const questions = useSelector((s: any) => s.questions);
    let foundQuestion = null;
    const events = questions.events;
    if (events) {
        for (const eventId in events) {
            for (const question of events[eventId].questions ?? []) {
                if (question.question_id == questionId) {
                    foundQuestion = question;
                    break;
                }
            }

            if(!foundQuestion) {
                for (const ticketId in events[eventId].ticketQuestions ?? {}) {
                    for (const question of events[eventId].ticketQuestions[ticketId] ?? []) {
                        if (question.question_id == questionId) {
                            foundQuestion = question;
                            break;
                        }
                    }
                }
            }

            if (foundQuestion) {
                break;
            }
        }
    }

    if(!foundQuestion) {
        for (const question of questions.orders ?? []) {
            if(question.question_id == questionId) {
                foundQuestion = question;
                break;
            }
        }
    }

    return foundQuestion;
}

function MemberLoginWrapper() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    dispatch(setCheckoutOverlay(true));
  }, []);

  return <MemberLoginContainer navigate={navigate} onLogin={location.state?.onLogin} />;
}
function EmailContainerWrapper() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const referer = queryParams.get('referer');
  const navigate = useNavigate();

  return <EmailContainer navigate={navigate} referer={referer} />;
}

function CheckoutRoutes() {
  return (
    <Routes>
      <Route path="/" element={<TicketPickerWrapper />} />
      <Route path="/picker-with-details" element={<PickerWithDetailsPage />} />
      <Route path="/tickets-reserved" element={<TicketsReservedModal />} />
      <Route path="/tickets-reserved/:token" element={<TicketsReservedModal />} />
      <Route path="/continue-shopping" element={<ContinueShoppingModal />} />
      <Route path="/continue-shopping/:token" element={<ContinueShoppingModal />} />
      <Route path="/more-time" element={<MoreTimeModal />} />
      <Route path="/checkout/:token" element={<CheckoutWrapper />} />
      <Route path="/cart/:token" element={<CartWrapper />} />
      <Route path="/confirmation/:confId" element={<ConfirmationWrapper />} />
      <Route path="/pending/:token" element={<PendingWrapper />} />
      <Route path="/promo" element={<CouponWrapper />} />
      <Route path="/auth" element={<AuthContainer />} />
      <Route path="/auth/email" element={<EmailContainerWrapper />} />
      <Route path="/day" element={<SeriesDatePickerWrapper />} />
      <Route path="/day/:listingSlug" element={<SeriesDatePickerWrapper />} />
      <Route path="/kiosk-time" element={<KioskTimePicker />} />
      <Route path="/waiver/:token" element={<KioskWaiverWrapper />} />
      <Route path="/member-login" element={<MemberLoginWrapper />} />
    </Routes>
  );
}

export default withRouterCompat(CheckoutRoutes)
